import {
  ResponseResult,
  ResponseStatus,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
  type SubResponse,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
} from './preserveResponseFields';

/**
 * Arguments for {@link buildCommentUpdate}. Bundled in a single
 * object so future fields (attachments draft, N/A reason draft,
 * `expectedLastUpdatedAt`) slot in without shifting the signature.
 */
export interface BuildCommentUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — sent as `respondedBy` / `lastUpdatedBy`. */
  readonly alias: string;
  /**
   * The draft comment map. Keys are either the parent `questionId`
   * (for the parent-level comment) or a `subQuestionId` (for a
   * sub-question's comment). Values are the current in-flight
   * strings from `useCommentDraft`. An empty / whitespace-only
   * string becomes `undefined` on the wire so the server clears
   * the field via its full-PATCH semantics.
   *
   * Keys not present in the draft fall back to preservation from
   * `previousResponse` / `subQuestionResponses` — that's how a
   * sub-question comment save avoids clobbering the parent's
   * saved comment (and vice-versa).
   */
  readonly commentDraft: Readonly<Record<string, string>>;
  /**
   * The parent question's cached response, or undefined for a
   * never-answered question. Drives `responseStatus`,
   * `responseResult`, and preservation of `naReason` / `mediaIds`
   * / `taskIds` on the parent slot.
   */
  readonly previousResponse: QuestionResponseDetails | undefined;
  /**
   * Existing sub-question responses on the parent. Every entry is
   * projected onto `subResponses` on the outgoing input with its
   * fields preserved and only its comment overridden from the
   * draft (when present). Undefined / empty means "no existing
   * sub-responses" — sub-question drafts still produce synthetic
   * entries; see docblock body.
   */
  readonly subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined;
}

/**
 * Builds the mutation input for a comment autosave.
 *
 * Pure — no react, no side effects. Consumed by
 * {@link useCommentDraft} to fire debounced saves without leaking
 * mutation-shape details into the hook.
 *
 * Design contract:
 *
 *   1. Result / status preservation. The mutation is a full-PATCH,
 *      so any field NOT re-asserted is cleared on the server. We
 *      copy `responseStatus` and (if set) `responseResult` from
 *      the cached parent response. Never-answered parent + typed
 *      comment falls back to `ResponseStatus.PENDING` with no
 *      result — matches the desktop pattern where a comment can
 *      be dropped before any Pass/Fail/NA tap.
 *
 *   2. Empty → undefined. A blank or whitespace-only draft comment
 *      is emitted as `undefined` (not `''`). The server treats
 *      unset as "clear this field", which is exactly what the
 *      auditor gets after selecting-all and deleting.
 *
 *   3. Per-slot tri-state. Each draft key resolves to one of:
 *      absent (fall back to persisted), present-non-blank (use
 *      draft), present-blank (clear — server field goes undefined
 *      on the round-trip). This makes empty-out-and-save actually
 *      erase the persisted comment, while never-typed rows keep
 *      their existing comment through unrelated comment saves.
 *      The tri-state is expressed by threading `commentDraft[id]`
 *      into {@link buildPreservedResponseData} /
 *      {@link projectSubResponsesForPreservation}: `undefined`
 *      (absent) → preserve, `''` (blank) → clear, non-blank → use.
 *
 *   4. Sub-response preservation. Every existing
 *      `SubQuestionResponseEntry` becomes a `SubResponse` on the
 *      write shape carrying result / comment / naReason /
 *      mediaIds / taskIds via the shared
 *      {@link projectSubResponsesForPreservation}. This is stronger
 *      than the pre-CR-V6 answer paths (result-only projection)
 *      but the CR-V6 refactor widened THOSE too — every
 *      full-PATCH write on this endpoint now funnels through the
 *      shared preservation helpers so a fix in one file lands in
 *      all three.
 *
 *   5. Synthetic sub-response entries. When the draft has a
 *      non-blank comment keyed by a `subQuestionId` that isn't
 *      present in the existing `subQuestionResponses`, a new
 *      `{ subQuestionId, comment }` entry is appended so the
 *      "typed a comment before answering" path can persist.
 *      Present-but-blank keys with no existing entry are dropped
 *      — there's nothing to clear.
 *
 *   6. `expectedLastUpdatedAt` is NOT set. Conflict detection is
 *      the CR-V11 slice; the summary payload's
 *      `QuestionResponseDetails` doesn't carry a per-record
 *      token today.
 */
export function buildCommentUpdate(
  args: BuildCommentUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const {
    businessUnitId,
    auditId,
    questionId,
    alias,
    commentDraft,
    previousResponse,
    subQuestionResponses,
  } = args;

  // Derive the write-side responseStatus from the previous result.
  // The summary payload's `responseStatus` uses a DIFFERENT enum
  // (`AuditQuestionCompletionStatus` = NOT_STARTED / IN_PROGRESS /
  // COMPLETED) than the mutation's write shape (`ResponseStatus` =
  // COMPLETED / NA / PENDING), so passing it through would be a
  // type error and a semantic mismatch. Derivation rule matches
  // `buildAnswerUpdate` for symmetry:
  //   NA        → ResponseStatus.NA
  //   PASS/FAIL → ResponseStatus.COMPLETED
  //   undefined → ResponseStatus.PENDING
  const responseResult = previousResponse?.responseResult;
  const responseStatus =
    responseResult === ResponseResult.NA
      ? ResponseStatus.NA
      : responseResult !== undefined
        ? ResponseStatus.COMPLETED
        : ResponseStatus.PENDING;

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId,
    auditId,
    questionId,
    responseStatus,
    respondedBy: alias,
    lastUpdatedBy: alias,
  };

  if (responseResult !== undefined) {
    input.responseResult = responseResult;
  }

  // Parent block. `readDraft(commentDraft, questionId)` under
  // noUncheckedIndexedAccess returns `string | undefined` —
  // undefined when the draft has no parent-level typing, in which
  // case the shared helper preserves the persisted comment.
  // Present '' → clears. Present non-blank → replaces.
  const parentBlock = buildPreservedResponseData(previousResponse, {
    commentOverride: readDraft(commentDraft, questionId),
  });
  if (parentBlock) {
    input.response = parentBlock;
  }

  // Sub-responses. Existing entries pass through the shared
  // projection with `commentOverrides = commentDraft` — the helper
  // applies the same tri-state per sub. Synthetic entries handle
  // the "comment typed before an answer was tapped" path below.
  const projected = projectSubResponsesForPreservation(subQuestionResponses, {
    commentOverrides: commentDraft,
  });
  const synthetic = buildSyntheticSubResponses(
    commentDraft,
    subQuestionResponses,
    questionId,
  );
  const combined = [...projected, ...synthetic];
  if (combined.length > 0) {
    input.subResponses = combined;
  }

  return input;
}

/**
 * Builds synthetic `SubResponse` entries for sub-question ids
 * present in the draft but NOT in the existing
 * `subQuestionResponses`. Only non-blank drafts count — a
 * present-but-blank key with no persisted entry has nothing to
 * clear, so we drop it silently.
 *
 * The parent `questionId` is filtered out here (it owns the
 * parent-level response block, not a sub-response row) so an
 * accidentally-mixed draft doesn't leak the parent comment into
 * `subResponses`.
 */
function buildSyntheticSubResponses(
  draft: Readonly<Record<string, string>>,
  existing: readonly SubQuestionResponseEntry[] | undefined,
  parentQuestionId: string,
): SubResponse[] {
  const existingIds = new Set((existing ?? []).map((entry) => entry.subQuestionId));
  const synthetic: SubResponse[] = [];
  for (const draftKey of Object.keys(draft)) {
    if (draftKey === parentQuestionId) continue;
    if (existingIds.has(draftKey)) continue;
    // noUncheckedIndexedAccess gives us `string | undefined`; the
    // hasOwnProperty check on Object.keys iteration guarantees a
    // string, but TypeScript still narrows to `string | undefined`.
    const raw = readDraft(draft, draftKey);
    if (raw === undefined) continue;
    const trimmed = raw.trim();
    if (trimmed === '') continue;
    synthetic.push({ subQuestionId: draftKey, comment: trimmed });
  }
  return synthetic;
}

/**
 * Safe reader for a draft slot. Isolates the dynamic-key access
 * so the security/detect-object-injection disable-comment lives in
 * one place. Callers get `string | undefined` verbatim — the
 * absent-vs-blank distinction is preserved.
 */
function readDraft(
  draft: Readonly<Record<string, string>>,
  key: string,
): string | undefined {
  // eslint-disable-next-line security/detect-object-injection
  return draft[key];
}
